// Persistent bottom bar: trust panel. Surfaces the published snapshot id +
// counts + last build's gate verdict on every route. R1 design lean is
// "footer not route" so it stays in front of the mentor's eyes — a
// non-zero drop count means there's something to do.
//
// Drop count uses the destructive tint; allowedUnapproved (preview build)
// promotes to a louder warning because shipping that to students would
// bypass the publish gate.

import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Database } from 'lucide-react';

export function TrustFooter() {
  const { data } = useQuery({
    queryKey: ['trust'],
    queryFn: () => api.trust(),
    // Trust state changes on re-seed, not on the second. Refetch lightly.
    staleTime: 60_000,
  });

  if (!data) {
    return (
      <footer className="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-[var(--color-subtle-foreground)]">
        loading snapshot…
      </footer>
    );
  }

  const { snapshotId, createdAt, conceptCount, artifactCount, gateReport } = data;
  const drops = gateReport ? gateReport.droppedNoApproval + gateReport.droppedStaleApproval : 0;
  const previewMode = gateReport?.allowedUnapproved ?? false;

  return (
    <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-[10px] tracking-[0.04em] text-[var(--color-subtle-foreground)]">
      <span className="inline-flex items-center gap-1.5">
        <Database className="size-3" strokeWidth={1.75} />
        <span className="font-mono text-[var(--color-foreground)]">{snapshotId}</span>
        <span>· {formatDate(createdAt)}</span>
      </span>
      <span>
        {conceptCount} concepts · {artifactCount} artifacts
      </span>
      {gateReport ? (
        drops > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-[var(--color-destructive)]">
            <AlertTriangle className="size-3" strokeWidth={1.75} />
            gate dropped {drops}/{gateReport.totalDrafts} (no-approval=
            {gateReport.droppedNoApproval}, stale=
            {gateReport.droppedStaleApproval})
          </span>
        ) : (
          <span>
            gate passed {gateReport.gatePassed}/{gateReport.totalDrafts}
          </span>
        )
      ) : (
        <span>gate report unavailable</span>
      )}
      {previewMode && (
        <span className="inline-flex items-center gap-1.5 rounded bg-[var(--color-destructive)] px-1.5 py-0.5 text-[var(--color-destructive-foreground)]">
          <AlertTriangle className="size-3" strokeWidth={2} />
          preview build · --allow-unapproved
        </span>
      )}
    </footer>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
